import { KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';
import { Image, ImageField, TextField } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import useEmblaCarousel from 'embla-carousel-react';
import { sanitize } from 'isomorphic-dompurify';

import {
  HexColor,
  isColorBright,
  resolveColorProperty,
  useBreakpoint,
  useFallbackTranslation,
} from '@gn-audio/jss-components';
import { tailwindConfig } from '@gn-audio/jss-components/tailwind-presets';

import ArrowSliderNavigation from 'common/components/JabraSlider/ArrowSliderNavigation';

export type ProductItem = {
  productUrl: TextField;
  productImage: ImageField;
  productName: TextField;
  buttonAriaLabel?: Field<string>;
};

export type VideoBannerProducts = {
  withProductsSection: Field<boolean>;
  productsList: ProductItem[];
  explanatoryText: Field<string>;
  explanatoryBackgroundColor: Field<string>;
};

export const VideoBannerProducts = (props: VideoBannerProducts) => {
  const { getURLWithLocalePrefix } = useFallbackTranslation();
  const { withProductsSection, productsList, explanatoryText, explanatoryBackgroundColor } = props;
  const { isAboveSm } = useBreakpoint('sm', true);
  const { isAboveXl } = useBreakpoint('xl', true);

  const [emblaRef, emblaApi] = useEmblaCarousel({ align: 'start' });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const totalSlides = emblaApi?.slideNodes().length || 0;
  const containerWith = totalSlides > 3 ? '' : 'lg:w-3/6';
  const multipleSlidesClassName =
    totalSlides > 3 ? 'lg:w-1/4 lg:basis-1/4 xl:w-1/5 xl:basis-1/5' : '';

  const canScrollNext = emblaApi?.canScrollNext() ?? false;
  const canScrollPrev = emblaApi?.canScrollPrev() ?? false;

  const sliderCentered = useMemo(() => {
    if (isAboveXl && productsList?.length < 5) {
      return true;
    } else if (isAboveSm && productsList?.length < 3) {
      return true;
    }

    return false;
  }, [isAboveXl, isAboveSm, productsList?.length]);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  if (!withProductsSection?.value) {
    return null;
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      emblaApi?.scrollPrev();
    } else if (event.key === 'ArrowRight') {
      emblaApi?.scrollNext();
    }
  };

  const defaultBackgroundColor = tailwindConfig.theme.colors.yellow as HexColor;
  const explanatoryBackgroundColorHex = !!explanatoryBackgroundColor?.value
    ? (resolveColorProperty(explanatoryBackgroundColor.value) ?? defaultBackgroundColor)
    : defaultBackgroundColor;
  const explanatoryTextClassName = explanatoryBackgroundColor?.value
    ? isColorBright(explanatoryBackgroundColorHex)
      ? 'text-black'
      : 'text-white'
    : 'text-white';

  return (
    <div
      className="absolute -bottom-[48px] z-10 flex w-full justify-center"
      onKeyDown={handleKeyDown}
    >
      <div className={`relative flex w-3/4 flex-col justify-end ${containerWith}`}>
        <div
          className="h-full w-full overflow-hidden rounded-t-3xl bg-[rgba(0,0,0,0.4)] py-4 backdrop-blur-md"
          ref={emblaRef}
        >
          <div className={classNames('flex', sliderCentered && 'justify-center')}>
            {productsList.map((el) => {
              const hasUrl = !!el.productUrl?.value;
              const arialabel = el.buttonAriaLabel?.value;
              const commonClassName = `group flex w-full shrink-0 grow-0 basis-full flex-col items-center rounded-md no-underline sm:w-1/2 sm:basis-1/2 md:w-1/3 md:basis-1/3 ${multipleSlidesClassName}`;

              const WrapperComponent = hasUrl ? 'a' : 'div';
              const wrapperProps = hasUrl
                ? {
                    href: getURLWithLocalePrefix(el.productUrl.value as string),
                    className: `${commonClassName} cursor-pointer`,
                    ...(arialabel && {
                      'aria-label': arialabel,
                    }),
                  }
                : {
                    className: commonClassName,
                  };
              const productKey = `${el.productName?.value}-${el.productUrl?.value}`;

              return (
                <WrapperComponent key={productKey} {...wrapperProps}>
                  <Image field={el.productImage} className="h-[196px] w-[196px]" loading="lazy" />
                  <div
                    className={classNames(
                      'rounded-full bg-gray-700 px-4 py-2 text-center text-base font-bold text-white shadow-sm transition-colors duration-300',
                      hasUrl && 'group-hover:bg-primary group-hover:text-on-brand'
                    )}
                  >
                    {el.productName.value}
                  </div>
                </WrapperComponent>
              );
            })}
          </div>
        </div>
        <ArrowSliderNavigation
          buttonVariant="primary"
          arrowsWrapperClass="!p-0 w-full h-full pointer-events-none"
          total={totalSlides}
          hideIfCantScroll={true}
          canScrollNext={canScrollNext}
          canScrollPrev={canScrollPrev}
          prevButtonClasses="absolute top-1/2 -translate-y-1/2 -left-6 pointer-events-auto z-30"
          nextButtonClasses="absolute top-1/2 -translate-y-1/2 -right-6 pointer-events-auto z-30"
          activeIndex={selectedIndex}
          onNext={() => {
            emblaApi?.scrollNext();
          }}
          onPrev={() => {
            emblaApi?.scrollPrev();
          }}
        />
        <p
          className={classNames(
            'flex min-h-12 w-full items-center justify-center rounded-b-3xl bg-primary text-center font-bold',
            explanatoryTextClassName
          )}
          dangerouslySetInnerHTML={{ __html: sanitize(explanatoryText?.value) }}
          style={{
            backgroundColor: explanatoryBackgroundColorHex,
          }}
        />
      </div>
    </div>
  );
};
